import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeIn } from './FadeIn';

const companies = [
  {
    name: "FirstShip",
    suffix: "Partners",
    focus: "Go-to-Market & Revenue Acceleration",
    desc: "Specialized GTM strategy and execution for B2B technology companies finding their initial scalable revenue motions.",
    cta: "Ship Your Product",
    url: "https://firstshippartners.com"
  },
  {
    name: "WeekOne",
    suffix: "Systems",
    focus: "AI Executive Assistant",
    desc: "A live AI executive assistant handling your inbox, calendar, and meetings within 24 hours — trained to sound like you by day seven.",
    cta: "Start Operating",
    url: "https://wk1.ai"
  },
  {
    name: "Hello",
    suffix: "Claw",
    focus: "AI Personal Assistant",
    desc: "Your personal AI assistant that feels human, lives in WhatsApp, and is set up within 24 hours — no subscriptions, no complexity.",
    cta: "Meet Claw",
    url: "https://myhelloclaw.com"
  },
  {
    name: "Meeting",
    suffix: "Foundry",
    focus: "Operational Cadence & Alignment",
    desc: "Structuring executive communication and meeting cadences to drive strategic alignment across scaling organizations.",
    cta: null
  }
];

export const Companies = () => {
  const [mobileOpen, setMobileOpen] = useState(null);

  const handleMobileSelect = (index) => {
    setMobileOpen(mobileOpen === index ? null : index);
  };

  return (
    <section id="companies" className="relative py-32 md:py-48 bg-c-dark noise-overlay overflow-hidden">
      <div aria-hidden="true" className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-[300px] bg-gradient-to-b from-c-mauve/10 to-transparent pointer-events-none"></div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16 md:mb-32">
          <FadeIn>
            <h2 className="text-xs font-medium tracking-super-wide uppercase text-c-powder mb-6">The Ecosystem</h2>
            <h3 className="text-3xl md:text-5xl font-light text-white leading-[1.2] mb-8">
              The <span className="font-wordmark text-c-cream">chRsMa</span> Companies
            </h3>
            <p className="text-lg text-c-powder/60 font-light leading-relaxed">
              Complementary businesses that extend our strategic thesis into specific operational domains. We don't just advise; we operate within the ecosystem we build.
            </p>
          </FadeIn>
        </div>

        {/* Desktop: 2-col horizontal cards */}
        <div className="hidden md:grid md:grid-cols-2 gap-6">
          {companies.map((company, index) => {
            const cardClasses = "group block h-full rounded-xl border border-c-navy-light/40 bg-c-navy/20 p-10 md:p-12 hover:border-transparent hover:shadow-glow-mauve transition-all duration-500 gradient-border";
            const cardContent = (
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 h-full">
                <div className="flex-1">
                  <p className="text-[10px] font-medium tracking-widest uppercase text-c-powder/40 mb-6">{company.focus}</p>
                  <h4 className="text-2xl md:text-3xl font-light text-white mb-2">
                    {company.name} <span className="font-serif-italic text-gradient-warm">{company.suffix}</span>
                  </h4>
                </div>
                <div className="flex-1 md:max-w-[55%]">
                  <p className="text-c-powder/60 font-light leading-relaxed mb-4">{company.desc}</p>
                  <span className="chrsma-badge">{company.url ? 'A CHRSMA Company' : 'A CHRSMA Company · Coming Soon'}</span>
                </div>
              </div>
            );

            return (
              <FadeIn key={index} delay={index * 0.1}>
                {company.url ? (
                  <a href={company.url} target="_blank" rel="noopener noreferrer" className={cardClasses}>
                    {cardContent}
                  </a>
                ) : (
                  <div className={cardClasses}>{cardContent}</div>
                )}
              </FadeIn>
            );
          })}
        </div>

        {/* Mobile: single-open accordion with always-visible gradient border */}
        <div className="md:hidden space-y-3">
          {companies.map((company, index) => (
            <FadeIn key={index} delay={index * 0.08}>
              <div className="rounded-xl border border-transparent bg-c-navy/20 overflow-hidden gradient-border-mobile">
                <button
                  onClick={() => handleMobileSelect(index)}
                  className="w-full text-left p-6"
                >
                  <p className="text-[10px] font-medium tracking-widest uppercase text-c-powder/40 mb-3">{company.focus}</p>
                  <h4 className="text-xl font-light text-white">
                    {company.name} <span className="font-serif-italic text-gradient-warm">{company.suffix}</span>
                  </h4>
                </button>

                <AnimatePresence>
                  {mobileOpen === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.21, 0.47, 0.32, 0.98] }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 pt-0">
                        <p className="text-c-powder/60 font-light leading-relaxed text-sm mb-5">{company.desc}</p>
                        {company.url ? (
                          <a
                            href={company.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] font-medium tracking-widest uppercase text-c-coral/60 hover:text-c-coral transition-colors"
                          >
                            {company.cta} →
                          </a>
                        ) : (
                          <span className="text-[11px] font-medium tracking-widest uppercase text-c-powder/30">Coming Soon</span>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
};
